import { hintMoreBelow } from './scroll-hint';

const TITLE_ID = 'nds-sheet-title';

/**
 * A place's details as a sheet over the widget, for a map too low to hold a popup. While it is open
 * the rest of the widget is inert (`behind`), the host page stays reachable — so it is a dialog of
 * the widget, not of the page. Escape, the close button and a tap beside it close it, and the focus
 * goes back to where it came from.
 */
export class DetailsSheet {
  /** in the DOM only while open */
  private readonly layer: HTMLElement;
  private readonly dialog: HTMLElement;
  private readonly body: HTMLElement;
  private returnFocus: HTMLElement | null = null;

  constructor(
    private readonly host: HTMLElement,
    private readonly behind: () => HTMLElement[],
  ) {
    this.layer = document.createElement('div');
    this.layer.className = 'sheet-layer';
    this.layer.innerHTML = `<div class="sheet-backdrop"></div>
      <section class="sheet" role="dialog" aria-labelledby="${TITLE_ID}" tabindex="-1">
        <button type="button" class="sheet-close" aria-label="Schließen">${CLOSE_ICON}</button>
        <div class="sheet-body"></div>
      </section>`;
    this.dialog = this.layer.querySelector('.sheet')!;
    this.body = this.layer.querySelector('.sheet-body')!;
    hintMoreBelow(this.body);
    this.layer.querySelector('.sheet-backdrop')!.addEventListener('click', () => this.close());
    this.layer.querySelector('.sheet-close')!.addEventListener('click', () => this.close());
    this.layer.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.close();
      }
    });
  }

  /** Show `html` (a place's details with a `.popup-title`); `returnFocus` gets the focus back on close. */
  open(html: string, returnFocus: HTMLElement): void {
    this.body.innerHTML = html;
    this.body.querySelector('.popup-title')?.setAttribute('id', TITLE_ID);
    this.returnFocus = returnFocus;
    if (!this.layer.isConnected) this.host.appendChild(this.layer);
    for (const el of this.behind()) el.inert = true;
    this.body.scrollTop = 0;
    this.dialog.focus();
  }

  /** `restoreFocus: false` when what had the focus is about to go (the map draws another answer). */
  close(restoreFocus = true): void {
    if (!this.layer.isConnected) return;
    for (const el of this.behind()) el.inert = false;
    this.layer.remove();
    if (restoreFocus && this.returnFocus?.isConnected) this.returnFocus.focus();
    this.returnFocus = null;
  }
}

const CLOSE_ICON = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>';
